import React from "react";
import { useNavigate, Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { getAuth } from "firebase/auth";

function TellMeAboutYourself() {
  const navigate = useNavigate();
  const auth = getAuth();

  const handleStart = () => {
    if (auth.currentUser) {
      navigate("/");
    } else {
      navigate("/get-started");
    }
  };

  return (
    <div style={{ padding: "40px", maxWidth: "900px", margin: "0 auto" }}>

      <SEOHead
        title="Tell Me About Yourself Answer (Best Examples 2026)"
        description="Learn how to answer 'Tell me about yourself' in interviews with proven examples and structure. Stand out and get hired faster with EmployPilot."
        canonical="https://employpilot.com/tell-me-about-yourself"
      />

      <h1>How to Answer “Tell Me About Yourself” (With Examples)</h1>

      <p style={{ fontSize: "18px", color: "#555" }}>
        “Tell me about yourself” is one of the most common interview questions—and one of the most important.
        Your answer sets the tone for the entire interview.
      </p>

      <p style={{ fontWeight: "bold", marginTop: "15px" }}>
        A strong answer shows who you are, what you bring, and why you’re a great fit—all in under 60 seconds.
      </p>

      <button onClick={handleStart} style={btnPrimary}>
        Practice My Answer
      </button>

      {/* SECTION */}
      <h2 style={{ marginTop: "40px" }}>What Interviewers Are Looking For</h2>

      <ul>
        <li>✔ A clear summary of your experience</li>
        <li>✔ Relevant skills for the role</li>
        <li>✔ Confidence and communication</li>
        <li>✔ A logical career story</li>
      </ul>

      {/* SECTION */}
      <h2 style={{ marginTop: "40px" }}>The Best Structure to Use</h2>

      <p>
        The easiest way to answer this question is using a simple structure:
      </p>

      <ul>
        <li><strong>Present:</strong> What you’re doing now</li>
        <li><strong>Past:</strong> Relevant experience</li>
        <li><strong>Future:</strong> Why you’re excited about this role</li>
      </ul>

      {/* SECTION */}
      <h2 style={{ marginTop: "40px" }}>Example Answers</h2>

      <h3>Entry-Level Example</h3>
      <p>
        I recently graduated and have been building my skills through projects and internships.
        I enjoy problem-solving and working in team environments, and I’m excited to apply my knowledge
        in a professional setting where I can continue to grow.
      </p>

      <h3>Professional Example</h3>
      <p>
        I have over five years of experience in my field, where I’ve focused on improving efficiency
        and delivering measurable results. I’ve led projects that increased performance and reduced costs,
        and I’m now looking for an opportunity to take on new challenges and contribute at a higher level.
      </p>

      <h3>Career Change Example</h3>
      <p>
        I’ve built a strong foundation in my previous field, but I’m now transitioning into a new area
        where I can apply my transferable skills like communication, leadership, and problem-solving.
        I’ve been actively learning and preparing, and I’m excited to bring that energy into this role.
      </p>

      {/* SECTION */}
      <h2 style={{ marginTop: "40px" }}>Common Mistakes to Avoid</h2>

      <ul>
        <li>❌ Talking too long</li>
        <li>❌ Sharing irrelevant personal details</li>
        <li>❌ Not connecting your answer to the job</li>
        <li>❌ Sounding unprepared</li>
      </ul>

      {/* SECTION */}
      <h2 style={{ marginTop: "40px" }}>Pro Tips to Stand Out</h2>

      <ul>
        <li>✔ Keep your answer under 60 seconds</li>
        <li>✔ Practice out loud</li>
        <li>✔ Focus on results and impact</li>
        <li>✔ Stay confident and natural</li>
      </ul>

      {/* INTERNAL LINKS */}
      <p style={{ marginTop: "20px" }}>
        Want to practice with AI?{" "}
        <Link to="/ai-interview-coach">
          Try the AI Interview Coach
        </Link>
      </p>

      <p>
        Preparing for more questions?{" "}
        <Link to="/interview-questions">
          See common interview questions
        </Link>
      </p>

      <p>
        Need a strong resume first?{" "}
        <Link to="/ai-resume-builder">
          Build your resume here
        </Link>
      </p>

      {/* COMMUNITY CTA */}
      <p style={{ marginTop: "30px" }}>
        Want feedback on your answer?{" "}
        <strong>Join the EmployPilot Community</strong> and get real input from others.
      </p>

      <p style={{ fontSize: "14px", color: "#777" }}>
        (Free account required)
      </p>

      <Link to="/community">
        Explore the Community
      </Link>

      {/* CTA */}
      <div style={{ marginTop: "50px", textAlign: "center" }}>
        <h2>Get Interview-Ready Today</h2>

        <button onClick={handleStart} style={btnSuccess}>
          Start Practicing
        </button>
      </div>

      {/* FAQ */}
      <h2 style={{ marginTop: "60px" }}>
        Frequently Asked Questions
      </h2>

      <h3>How long should my answer be?</h3>
      <p>
        Keep it between 30–60 seconds to stay clear and engaging.
      </p>

      <h3>Should I talk about personal life?</h3>
      <p>
        Keep it professional and relevant to the role you’re applying for.
      </p>

      <h3>Can AI help me practice interview answers?</h3>
      <p>
        Yes. EmployPilot can help you practice and improve your responses.
      </p>
<div
    style={{
      marginTop: "80px",
      textAlign: "center",
      fontSize: "14px",
      color: "#777",
      borderTop: "1px solid #eee",
      paddingTop: "30px"
    }}
  >
    © 2026 EmployPilot. All Rights Reserved.

    <div style={{ marginTop: "10px" }}>
      <a href="/terms" style={{ margin: "0 10px" }}>
        Terms of Use
      </a>

      <a href="/privacy" style={{ margin: "0 10px" }}>
        Privacy Policy
      </a>

      <a href="/contact" style={{ margin: "0 10px" }}>
        Contact
      </a>
    </div>
  </div>
</div>
  );
}

const btnPrimary = {
  backgroundColor: "#007bff",
  color: "#fff",
  padding: "12px 20px",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  marginTop: "20px"
};

const btnSuccess = {
  backgroundColor: "#28a745",
  color: "#fff",
  padding: "14px 24px",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer"
};

export default TellMeAboutYourself;